import {
  Activity, AudioLines, AudioWaveform, BookUser, Building2, CalendarDays, CircleUser, Globe, GraduationCap, Hash,
  History, Inbox, Landmark, ListChecks, ListPlus, ListTodo, Mail, Megaphone, MessageCircleQuestion, MessageSquareText,
  MessageCircleMore, MessagesSquare, Milestone, Network, PhoneCall, PhoneIncoming, PhoneOff,
  PhoneOutgoing, PlugZap, Radio, Repeat, Server, Settings, Sparkles, SquareKanban, Target, Users, UsersRound,
  TextQuote, Voicemail, Waypoints, Webhook, Workflow,
  type LucideIcon,
} from "lucide-react";
import { atLeast, type Role } from "@/lib/types";

/**
 * The console's route map, in one place.
 *
 * The sidebar, the Settings page, the post-login redirect and the per-route guard all read from
 * here. When they were three separate lists, signing in could land somebody on a page their role
 * cannot open — the guard said no, the redirect did not know that, and the nav simply omitted the
 * link.
 *
 * A page goes in one of two places. The sidebar (`NAV_SECTIONS`) is for the pages somebody works
 * in most days: the phone, contacts, campaigns, the floor, the reports. Everything that is set up
 * once and changed now and then — a catalog, a library, the telephony, an integration — goes in
 * `SETTINGS_SECTIONS`, one click down behind Settings at the foot of the sidebar. The sidebar held
 * both until it ran to 27 links for a superadmin; asking "does somebody open this most days?" of
 * each new page is what keeps it short.
 */

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Hidden from the nav and refused by the guard below this role. */
  minRole: Role;
  /** A short tag after the label, e.g. to mark a screen that is still a placeholder. */
  badge?: string;
  /** A small heading drawn above this item, grouping it with the ones after it in its section. */
  subheading?: string;
  /** The full name, where the label leans on the subheading ("All" under Personal Inbox): the
   *  folded sidebar's tooltip and the link's accessible name. */
  fullLabel?: string;
  /** One line under the label on the Settings page's card: what the page is for. */
  description?: string;
}

export interface NavSection {
  heading: string;
  items: NavItem[];
}

export const NAV_SECTIONS: NavSection[] = [
  {
    heading: "Workspace",
    items: [
      { to: "/phone", label: "Phone", icon: PhoneCall, minRole: "rep" },
      // Everybody's own calls, including a rep's — which is the point. `/calls` below is the
      // floor's report and stays at supervisor; these are two screens because they answer two
      // questions ("how did my day go" vs "what is the floor doing"), and because a rep must
      // never be one cleared filter away from the second.
      { to: "/history", label: "Call history", icon: History, minRole: "rep" },
      // Every exchange with a contact: texts, calls and the team's comments (ADR-095). Every signed-in
      // person, as Contacts is. One entry for every inbox: `/conversations` opens the Team Inbox, the
      // personal ones are picked on the page, and it stays marked on all of them.
      { to: "/conversations", label: "Conversations", icon: MessagesSquare, minRole: "rep" },
      // The team's own messages (platform ADR-186): team chats and direct ones. Its unread count
      // is set by the app shell, which reads it live.
      { to: "/messages", label: "Team Chat", icon: MessageCircleMore, minRole: "rep" },
    ],
  },
  {
    heading: "CRM",
    items: [
      // Every signed-in person, as `GET /leads` is: a rep sees the contacts of the campaigns whose
      // lines they staff and the ones they have worked, a supervisor the tenant's (ADR-044).
      { to: "/contacts", label: "Contacts", icon: BookUser, minRole: "rep" },
      // The same contacts as a board, a column per stage (ADR-078): the same scope as the list, so
      // everyone who has the list has the board.
      { to: "/opportunities", label: "Opportunities", icon: SquareKanban, minRole: "rep" },
      // A person's to dos about leads, made by workflows (ACL-408). Everyone: a rep given a task
      // must find it; `GET /tasks` lists the caller's own, and a supervisor's everybody's on ask.
      { to: "/tasks", label: "Tasks", icon: ListTodo, minRole: "rep" },
      // Calendars and their appointments (ADR-085), with the calendar settings as one of the page's
      // tabs rather than a card behind Settings, as GoHighLevel has it (ACL-252). Everyone: a rep
      // works their own appointments there (ACL-292). The settings are `CALENDAR_SETTINGS_ITEM`'s.
      { to: "/calendars", label: "Calendars", icon: CalendarDays, minRole: "rep" },
      // The client's sheet as a table (ADR-045): supervisors read it, admins edit it. The firms are
      // records the floor looks things up in, like the contacts, not settings.
      { to: "/firms", label: "Firms", icon: Landmark, minRole: "supervisor" },
    ],
  },
  {
    heading: "People",
    items: [
      { to: "/users", label: "Users", icon: Users, minRole: "supervisor" },
      // Groups of people with a lead, and the campaign lines they staff (platform ADR-185).
      { to: "/teams", label: "Teams", icon: Network, minRole: "supervisor" },
    ],
  },
  {
    heading: "Contact Center",
    items: [
      { to: "/campaigns", label: "Campaigns", icon: Megaphone, minRole: "supervisor" },
      { to: "/floor", label: "Live floor", icon: Activity, minRole: "supervisor" },
    ],
  },
  {
    heading: "Analytics",
    items: [
      // The three reports (ADR-074), each its own screen: `/reports/*` is supervisor-and-above, as
      // `/acd/stats` is.
      { to: "/reports/lines", label: "Line report", icon: PhoneIncoming, minRole: "supervisor" },
      { to: "/reports/campaigns", label: "Campaign report", icon: Target, minRole: "supervisor" },
      { to: "/reports/reps", label: "Rep report", icon: UsersRound, minRole: "supervisor" },
      { to: "/calls", label: "Calls", icon: Waypoints, minRole: "supervisor" },
    ],
  },
];

/** The Settings page, and the entry at the foot of the sidebar that opens it. */
export const SETTINGS_ITEM: NavItem = {
  to: "/settings",
  label: "Settings",
  icon: Settings,
  minRole: "rep",
};

/**
 * The console's changelog (ACL-631): what is new, better or mended, for the whole team. At the foot
 * of the sidebar over Settings, not in the list: it is read now and then, not worked in, and the
 * foot keeps it in one place however long the list grows. Never behind the bell (`lib/system-updates.ts`).
 */
export const SYSTEM_UPDATES_ITEM: NavItem = {
  to: "/system-updates",
  label: "System updates",
  icon: Sparkles,
  minRole: "rep",
};

/**
 * The pages behind Settings. The headings are the ones these pages had in the sidebar, so a page
 * is found under the name people already looked for it by.
 */
export const SETTINGS_SECTIONS: NavSection[] = [
  {
    heading: "Contact Center",
    items: [
      // The follow-up policies a campaign runs (ADR-066): supervisors read them, admins edit.
      {
        to: "/cadences", label: "Cadences", icon: Repeat, minRole: "supervisor",
        description: "When a lead is called next, and how often. A campaign runs one from its Dialing tab.",
      },
      // `GET /skills` is supervisor-and-above: a supervisor reads the catalog, an admin edits it.
      {
        to: "/skills", label: "Skills", icon: GraduationCap, minRole: "supervisor",
        description: "What a line, a number or a handoff needs in the rep it rings.",
      },
      // A rep reads `GET /dispositions` too — their buttons after a call are this list — but on the
      // phone. The catalog is a supervisor's page to read and an admin's to edit.
      {
        to: "/dispositions", label: "Dispositions", icon: ListChecks, minRole: "supervisor",
        description: "The outcomes a rep picks after a call, and what each one does to the lead.",
      },
      // Supervisors read the store, as they read the campaign settings that pick from it; the API
      // lets only admins upload, rename and delete, and the page follows it.
      {
        to: "/audio", label: "Audio", icon: AudioLines, minRole: "supervisor",
        description: "The sound files your lines, campaigns and voicemail messages play.",
      },
      // Everyone signed in reads `GET /voicemail-messages` — a rep picks one on a dialer call — but on
      // the phone. The library names each recording from `GET /audio/library`, which is
      // supervisor-and-above, so it is a supervisor's page to read and an admin's to edit.
      {
        to: "/voicemail-messages", label: "Voicemail messages", icon: Voicemail, minRole: "supervisor",
        description: "The messages a rep can leave when a dialer call reaches voicemail.",
      },
      // Everyone signed in reads `GET /speed-dials` — a rep picks one from Add participant on a
      // call — but on the phone. The list is a supervisor's page to read and an admin's to edit.
      {
        to: "/speed-dials", label: "Speed dials", icon: PhoneOutgoing, minRole: "supervisor",
        description: "Outside numbers a rep can bring into a call by name.",
      },
      // Everyone signed in reads `GET /blocked-callers` and blocks from the phone, for up to a day
      // (ADR-159). The list, blocking for good and unblocking early are a supervisor's.
      {
        to: "/blocked-callers", label: "Blocked callers", icon: PhoneOff, minRole: "supervisor",
        description: "Callers turned away before any phone rings, for a while or for good.",
      },
    ],
  },
  {
    heading: "CRM",
    items: [
      // The lifecycle a contact moves through (ADR-077). Everyone signed in reads `GET /stages` —
      // every stage label does — but the catalog is a supervisor's page to read and an admin's to edit.
      {
        to: "/stages", label: "Stages", icon: Milestone, minRole: "supervisor",
        description: "The lifecycle every contact moves through, and what each stage means for calling.",
      },
      // The fields contacts and firms carry beyond the platform's own, their folders, and who may see and
      // edit each (ACL-255, ACL-256). Everyone signed in reads a record's fields, but defining them is an
      // admin's: every route under `/forms/{entity}` the page calls refuses anybody below.
      {
        to: "/custom-fields", label: "Custom fields", icon: ListPlus, minRole: "admin",
        description: "The fields contacts and firms carry, in folders, with who can see and edit each.",
      },
      // The questions a rep is asked on a call, and what each answer leads to (ACL-338). Admin
      // only, as the builder's routes are: the flow decides what every rep asks.
      {
        to: "/intake-flow", label: "Intake flow", icon: MessageCircleQuestion, minRole: "admin",
        description: "The questions a rep is asked during a call, and what each answer leads to.",
      },
    ],
  },
  {
    heading: "Automation",
    items: [
      // Admins only, as core-api's `/workflows` routes are: a workflow texts leads in the tenant's
      // name (ADR-111 in platform). Behind Settings: built once and changed now and then.
      {
        to: "/workflows", label: "Workflows", icon: Workflow, minRole: "admin",
        description: "What happens to a lead by itself after something happens: wait, then text them.",
      },
      // Admins only too: a template is what a workflow texts leads in the tenant's name (ACL-385).
      {
        to: "/text-templates", label: "Text templates", icon: TextQuote, minRole: "admin",
        description: "The texts a workflow sends a lead, written once in English and Spanish.",
      },
      // And the emails a workflow's Send email step sends (ACL-378), with their subjects.
      {
        to: "/email-templates", label: "Email templates", icon: Mail, minRole: "admin",
        description: "The emails a workflow sends a lead, each with its subject, in English and Spanish.",
      },
    ],
  },
  {
    heading: "Telephony",
    items: [
      {
        to: "/numbers", label: "Numbers", icon: Hash, minRole: "supervisor",
        description: "The phone numbers you hold, and where a call to each one goes.",
      },
      {
        to: "/endpoints", label: "Endpoints", icon: Server, minRole: "supervisor",
        description: "The phones and devices registered on the switch.",
      },
      {
        to: "/domains", label: "Domains", icon: Globe, minRole: "supervisor",
        description:
          "The SIP realms phones register in, with their outbound routing, transcription and how long calls are kept.",
      },
      // Trunks carry the carrier username and are what every domain's calls ride, so the
      // API refuses anyone below admin — the nav must agree or the link 403s on click.
      {
        to: "/gateways", label: "Gateways", icon: Radio, minRole: "admin",
        description: "The SIP trunks calls leave on.",
      },
    ],
  },
  {
    heading: "Integrations",
    items: [
      // Admins only, as core-api's `/webhooks/*` routes are: an endpoint sends the tenant's events,
      // with its leads' names and numbers, to a URL outside the platform (ADR-022).
      {
        to: "/slack", label: "Slack", icon: MessagesSquare, minRole: "admin",
        description: "Connect a Slack workspace, then pick its channels and events in Webhooks.",
      },
      {
        to: "/webhooks", label: "Webhooks", icon: Webhook, minRole: "admin",
        description: "Send the events you choose to Slack, Zapier or your CRM.",
      },
      // Admins only, as core-api's `/integrations/sms` is: the settings hold the account every
      // text is sent and paid through (ADR-099 in platform).
      {
        to: "/sms", label: "SMS", icon: MessageSquareText, minRole: "admin",
        description: "The provider your texts leave through, its account, and the numbers that send.",
      },
      // Admins only, as core-api's `/integrations/email` is: the settings hold the key of the account
      // every email to a contact is sent through (ADR-180 in platform).
      {
        to: "/email", label: "Email", icon: Mail, minRole: "admin",
        description: "The provider your emails to contacts leave through, the address they come from, and where replies arrive.",
      },
      // Admins only, as core-api's `/integrations/voice` is: the settings hold the key of the account
      // every greeting is spoken and paid through (ADR-114 in platform).
      {
        to: "/voices", label: "Voices", icon: AudioWaveform, minRole: "admin",
        description: "Your ElevenLabs account, and the voices that speak your voicemail greetings.",
      },
      // Admins only too: a trigger's URL can add leads to a dial list, hold them, and mark them
      // do-not-call (ADR-021).
      {
        to: "/triggers", label: "Triggers", icon: PlugZap, minRole: "admin",
        description: "Let another system act on your leads through a URL of its own.",
      },
    ],
  },
  {
    heading: "Platform",
    items: [
      {
        to: "/inventory", label: "Number inventory", icon: Inbox, minRole: "superadmin",
        description: "Every number the platform holds, who has it, and the requests waiting on you.",
      },
      {
        to: "/tenants", label: "Tenants", icon: Building2, minRole: "superadmin",
        description: "The organisations on the platform. Every user but a superadmin is in one.",
      },
    ],
  },
];

/**
 * The signed-in person's own page. It is on the Settings page, last, but it is every role's, so
 * it is not on its own a reason to show Settings: a rep reaches it from their menu.
 */
export const ACCOUNT_ITEM: NavItem = {
  to: "/settings/account",
  label: "Account",
  icon: CircleUser,
  minRole: "rep",
  description: "Your details, your password and your phone.",
};

/**
 * A calendar's settings page (`/calendars/settings/3`): under the Calendars page, which is every
 * role's, but a supervisor's to read. In no menu — the Calendars page's settings tab opens it — so
 * it is here only for the guard.
 */
export const CALENDAR_SETTINGS_ITEM: NavItem = {
  to: "/calendars/settings",
  label: "Calendar settings",
  icon: CalendarDays,
  minRole: "supervisor",
};

const ALL_ITEMS = [...NAV_SECTIONS, ...SETTINGS_SECTIONS]
  .flatMap((s) => s.items)
  .concat(ACCOUNT_ITEM, CALENDAR_SETTINGS_ITEM, SYSTEM_UPDATES_ITEM);

function filterSections(sections: NavSection[], role: Role | undefined): NavSection[] {
  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => atLeast(role, item.minRole)),
    }))
    .filter((section) => section.items.length > 0);
}

/** This path, or a page under it — what `NavLink` matches on without `end`. */
function under(path: string, to: string): boolean {
  return path === to || path.startsWith(`${to}/`);
}

/** The sidebar's sections, as far as this role can see them, without the pages in `hidden`: one
 *  the core-api behind the console does not have yet (Tasks before ACL-408's core-api). */
export function visibleSections(role: Role | undefined, hidden: readonly string[] = []): NavSection[] {
  return filterSections(NAV_SECTIONS, role)
    .map((section) => ({ ...section, items: section.items.filter((item) => !hidden.includes(item.to)) }))
    .filter((section) => section.items.length > 0);
}

/** The Settings page's sections, as far as this role can see them — without the account. */
export function visibleSettingsSections(role: Role | undefined): NavSection[] {
  return filterSections(SETTINGS_SECTIONS, role);
}

/** Whether this role has anything behind Settings but its own account, and so sees the entry. */
export function hasSettings(role: Role | undefined): boolean {
  return visibleSettingsSections(role).length > 0;
}

/**
 * Whether a path is somewhere behind Settings: the page itself, the account, one of its pages, or
 * a record on one (`/webhooks/3`). The sidebar marks Settings as the place you are on all of them,
 * since none of them has a link of its own there.
 */
export function isSettingsPath(path: string): boolean {
  if (under(path, SETTINGS_ITEM.to)) return true;
  return SETTINGS_SECTIONS.some((s) => s.items.some((item) => under(path, item.to)));
}

/**
 * The "← Settings" link a page reached from the Settings page shows over its title, or null.
 * Only on the pages the cards open: a record under one (`/webhooks/3`) already links back to its
 * list. Not for a role with no Settings page to go back to — a rep on their account.
 */
export function settingsBackLink(
  role: Role | undefined,
  path: string,
): { to: string; label: string } | null {
  if (!hasSettings(role)) return null;
  const onSettingsPage =
    path === ACCOUNT_ITEM.to || SETTINGS_SECTIONS.some((s) => s.items.some((item) => item.to === path));
  return onSettingsPage ? { to: SETTINGS_ITEM.to, label: SETTINGS_ITEM.label } : null;
}

/** Where this role belongs when it has not asked for anywhere in particular. */
export function landingPath(role: Role | undefined): string {
  return atLeast(role, "supervisor") ? "/users" : "/phone";
}

/**
 * Whether a role may open a path. Unknown paths (`/settings`, and anything not in the nav)
 * return true — they are open to every signed-in user, and defaulting to false would lock
 * people out of pages as they are added.
 */
export function canAccess(role: Role | undefined, path: string): boolean {
  // The most specific item rules: `/calendars/settings/3` is the settings page's, not the
  // Calendars page's, though it is under both.
  const item = ALL_ITEMS.filter((i) => under(path, i.to)).sort((a, b) => b.to.length - a.to.length)[0];
  return item ? atLeast(role, item.minRole) : true;
}

/**
 * A link to one person's record: their contact page, or their opportunity open on the board. What a
 * lead link from Slack is, and what a session that ran out in the middle of an edit was on.
 */
export function isRecordLink(intended: string): boolean {
  const [path = "", query = ""] = intended.split("?");
  if (/^\/contacts\/[^/]+$/.test(path)) return true;
  return path === "/opportunities" && new URLSearchParams(query).has("lead");
}

/**
 * Where to send someone after signing in.
 *
 * A rep starts on the Phone (ACL-553), unless they were sent to sign in from a link to one record
 * (`isRecordLink`): the Phone is where a call arrives and where they say whether they are taking
 * calls, and a rep signed in onto a list page was Ready, or ringing, on a page that showed neither.
 * A record they followed a link to is the one page worth going back to, and Taking calls is on
 * every page (`TakingCallsStrip`).
 *
 * Anybody else goes back to the page they were trying to reach, unless their role cannot open it —
 * a supervisor bounced off a page for admins should not be bounced straight back onto it after
 * logging in, which is the loop this prevents.
 */
export function postLoginPath(role: Role | undefined, intended: string | undefined): string {
  if (!intended || intended === "/login") return landingPath(role);
  if (role === "rep" && !isRecordLink(intended)) return landingPath(role);
  const path = intended.split("?")[0] ?? intended;
  return canAccess(role, path) ? intended : landingPath(role);
}
